import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Table, Tag, Button, Space, Modal, Form, Input, Select, Popconfirm, message, Typography, Empty } from 'antd';
import { SwapOutlined, KeyOutlined, LogoutOutlined, UserSwitchOutlined, DeleteOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import { ROLES } from '_helpers/roles';
import { normalizeVietnamese } from '_helpers/normalize';
import { useImpersonation } from './impersonation';
import { MongoRef } from './MongoRef';

const { Text } = Typography;
const { Option } = Select;

export { UsersTab };

// Role display label + tag colour, mirrors StaffManagePage's ROLE_META but covers all 5 roles.
const ROLE_META = {
    [ROLES.STUDENT]:    { label: 'Học sinh',   color: 'green'  },
    [ROLES.TEACHER]:    { label: 'Giáo viên',  color: 'blue'   },
    [ROLES.MANAGER]:    { label: 'Quản lý',    color: 'gold'   },
    [ROLES.HEADMASTER]: { label: 'Hiệu trưởng', color: 'purple' },
    [ROLES.ADMIN]:      { label: 'Admin',      color: 'red'    },
};

const STATUS_META = {
    active:    { label: 'Đang học',  color: 'green'   },
    inactive:  { label: 'Ngừng',     color: 'default' },
    suspended: { label: 'Bảo lưu',   color: 'orange'  },
};

function UsersTab() {
    const fetchWrapper = useFetchWrapper();
    const { impersonate } = useImpersonation();

    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch]   = useState('');
    const [roleModal, setRoleModal] = useState(null); // null | { record }
    const [saving, setSaving]   = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/admin/users', null, null);
            const json = await res.json();
            if (json.status === 'Success') setRows(Array.isArray(json.message) ? json.message : []);
            else message.error(typeof json.message === 'string' ? json.message : 'Không tải được danh sách người dùng');
        } catch (e) {
            message.error('Không tải được danh sách người dùng');
        }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = normalizeVietnamese(search.trim());
        if (!q) return rows;
        return rows.filter(r => {
            const hay = normalizeVietnamese(`${r.name || ''} ${r.student_id || ''} ${r.email || ''}`);
            return hay.includes(q);
        });
    }, [rows, search]);

    function openRoleModal(record) {
        form.setFieldsValue({ role: record.role });
        setRoleModal({ record });
    }

    async function handleSaveRole() {
        let values;
        try { values = await form.validateFields(); } catch { return; }
        setSaving(true);
        try {
            const res  = await fetchWrapper.post(
                `/api/admin/users/${roleModal.record.student_id}/role`,
                'application/json',
                JSON.stringify({ role: values.role })
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã đổi vai trò');
                setRoleModal(null);
                await load();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Đổi vai trò thất bại');
            }
        } catch (e) {
            message.error('Đổi vai trò thất bại');
        }
        setSaving(false);
    }

    async function handleResetPassword(record) {
        try {
            const res  = await fetchWrapper.post(`/api/admin/users/${record.student_id}/reset-password`, null, null);
            const json = await res.json();
            if (json.status === 'Success') { message.success('Đã đặt lại mật khẩu về mặc định'); await load(); }
            else message.error(typeof json.message === 'string' ? json.message : 'Đặt lại thất bại');
        } catch (e) {
            message.error('Đặt lại thất bại');
        }
    }

    async function handleForceLogout(record) {
        try {
            const res  = await fetchWrapper.post(`/api/admin/users/${record.student_id}/logout`, null, null);
            const json = await res.json();
            if (json.status === 'Success') { message.success('Đã đăng xuất người dùng'); await load(); }
            else message.error(typeof json.message === 'string' ? json.message : 'Đăng xuất thất bại');
        } catch (e) {
            message.error('Đăng xuất thất bại');
        }
    }

    async function handleDelete(record) {
        try {
            const url = record.role === ROLES.STUDENT
                ? `/api/manager/students/${record._id}`
                : `/api/manager/staff/${record._id}`;
            const res  = await fetchWrapper.delete(url, null, null);
            const json = await res.json();
            if (json.status === 'Success') { message.success('Đã xóa'); await load(); }
            else message.error(typeof json.message === 'string' ? json.message : 'Xóa thất bại');
        } catch (e) {
            message.error('Xóa thất bại');
        }
    }

    const columns = [
        { title: 'Mã', dataIndex: 'student_id', key: 'student_id', width: 110 },
        { title: 'Mongo', key: 'mongo', width: 120, render: (_, r) => <MongoRef id={r._id} collection="users" /> },
        { title: 'Tên', dataIndex: 'name', key: 'name', render: v => v || <Text type="secondary">—</Text> },
        {
            title: 'Vai trò',
            key: 'role',
            width: 130,
            filters: Object.entries(ROLE_META).map(([v, m]) => ({ text: m.label, value: v })),
            onFilter: (v, r) => r.role === v,
            render: (_, r) => {
                const m = ROLE_META[r.role] || { label: r.role, color: 'default' };
                return <Tag color={m.color}>{m.label}</Tag>;
            },
        },
        { title: 'Email', dataIndex: 'email', key: 'email', render: v => v || <Text type="secondary">—</Text> },
        { title: 'SĐT', dataIndex: 'phone_number', key: 'phone_number', width: 130, render: v => v || <Text type="secondary">—</Text> },
        {
            title: 'Trạng thái',
            dataIndex: 'enrollment_status',
            key: 'enrollment_status',
            width: 110,
            render: v => {
                const m = STATUS_META[v] || { label: v || '—', color: 'default' };
                return <Tag color={m.color}>{m.label}</Tag>;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 220,
            render: (_, r) => (
                <Space size={4} wrap>
                    <Button size="small" icon={<SwapOutlined />} title="Đổi vai trò" onClick={() => openRoleModal(r)} />
                    <Popconfirm
                        title={`Đặt lại mật khẩu của "${r.name}" về mặc định?`}
                        onConfirm={() => handleResetPassword(r)}
                        okText="Đặt lại" cancelText="Hủy"
                    >
                        <Button size="small" icon={<KeyOutlined />} title="Đặt lại mật khẩu" />
                    </Popconfirm>
                    <Popconfirm
                        title={`Đăng xuất "${r.name}" khỏi mọi phiên?`}
                        onConfirm={() => handleForceLogout(r)}
                        okText="Đăng xuất" cancelText="Hủy"
                    >
                        <Button size="small" icon={<LogoutOutlined />} title="Đăng xuất" />
                    </Popconfirm>
                    <Button
                        size="small"
                        icon={<UserSwitchOutlined />}
                        title="Mạo danh"
                        onClick={() => impersonate(r.student_id)}
                    />
                    <Popconfirm
                        title={`Xóa "${r.name}"? Hành động này không thể hoàn tác.`}
                        onConfirm={() => handleDelete(r)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} title="Xóa" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="soft-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ margin: 0 }}>Người dùng</h2>
                    <Text type="secondary">{filtered.length} / {rows.length} người dùng</Text>
                </div>
                <Input.Search
                    allowClear
                    placeholder="Tìm theo tên, mã, email..."
                    style={{ maxWidth: 320 }}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <Table
                columns={columns}
                dataSource={filtered.map(r => ({ ...r, key: r._id }))}
                loading={loading}
                pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                locale={{ emptyText: <Empty description="Không có người dùng" /> }}
                scroll={{ x: true }}
            />

            <Modal
                title={`Đổi vai trò — ${roleModal?.record?.name || ''}`}
                visible={!!roleModal}
                onCancel={() => setRoleModal(null)}
                onOk={handleSaveRole}
                confirmLoading={saving}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="role" label="Vai trò" rules={[{ required: true, message: 'Chọn vai trò' }]}>
                        <Select>
                            {Object.entries(ROLE_META).map(([v, m]) => <Option key={v} value={v}>{m.label}</Option>)}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
